"use client";

import { useState } from "react";
import Link from "next/link";
import {
  FeedGroup,
  groupEvents,
  type FeedEvent,
} from "@/components/feed/feed-group";
import {
  PhotoLightbox,
  type LightboxPhoto,
} from "@/components/feed/photo-lightbox";

const PAGE_SIZE = 20;

/**
 * The first page is rendered on the server and handed down as `initialEvents`
 * so the gallery has real content in the HTML; this component only owns the
 * interactive parts — "voir plus" and the lightbox.
 */
export function GalleryFeed({ initialEvents }: { initialEvents: FeedEvent[] }) {
  const [events, setEvents] = useState<FeedEvent[]>(initialEvents);
  const [hasMore, setHasMore] = useState(initialEvents.length === PAGE_SIZE);
  const [loadingMore, setLoadingMore] = useState(false);
  const [lightbox, setLightbox] = useState<{
    photos: LightboxPhoto[];
    index: number;
  } | null>(null);

  async function loadMore() {
    setLoadingMore(true);
    try {
      const res = await fetch(`/api/public/feed?offset=${events.length}`, {
        cache: "no-store",
      });
      if (!res.ok) {
        setHasMore(false);
        return;
      }
      const data = (await res.json()) as FeedEvent[];
      setEvents((prev) => [...prev, ...data]);
      setHasMore(data.length === PAGE_SIZE);
    } finally {
      setLoadingMore(false);
    }
  }

  if (events.length === 0) {
    return (
      <div className="text-center py-20 card">
        <div className="text-5xl mb-4">🌱</div>
        <h2 className="text-headline-sm text-garden-800">
          Le feed est encore vide
        </h2>
        <p className="text-body-md text-on-surface-variant mt-2 max-w-md mx-auto">
          Soyez le premier à partager votre potager ! Rendez-le public depuis
          l&apos;éditeur.
        </p>
        <Link href="/essayer" className="btn-primary inline-flex mt-4">
          Dessiner mon potager
        </Link>
      </div>
    );
  }

  const groups = groupEvents(events);

  return (
    <>
      <div className="space-y-6">
        {groups.map((group) => (
          <FeedGroup
            key={group[0].id}
            events={group}
            onPhotoClick={(photos, index) => setLightbox({ photos, index })}
          />
        ))}

        {hasMore && (
          <button
            onClick={loadMore}
            disabled={loadingMore}
            className="w-full py-3 text-label-lg text-primary hover:bg-surface-container rounded-xl transition-colors disabled:opacity-50"
          >
            {loadingMore ? "Chargement…" : "Voir plus"}
          </button>
        )}
      </div>

      <PhotoLightbox
        photos={lightbox?.photos ?? []}
        index={lightbox?.index ?? null}
        onClose={() => setLightbox(null)}
        onIndexChange={(index) =>
          setLightbox((prev) => (prev ? { ...prev, index } : prev))
        }
      />
    </>
  );
}
